/* eslint-disable @typescript-eslint/no-explicit-any */

import {useEffect, useRef} from 'react';

/**
 * Runs a side effect on every render **except** the initial mount — the
 * functional equivalent of the class-component `componentDidUpdate` lifecycle.
 *
 * Uses a `useRef` flag internally to skip the first `useEffect` invocation.
 * After that, `func` is called on every subsequent render where any `deps`
 * value has changed, following the same semantics as `useEffect`.
 *
 * @param func - Effect callback, identical in signature to the first argument
 *   of `useEffect`. May optionally return a cleanup function.
 * @param deps - Dependency array passed directly to the underlying `useEffect`.
 *   `func` is called whenever any dependency changes (after the initial mount).
 *
 * @example
 * ```tsx
 * useComponentDidUpdate(() => {
 *   analytics.track('filter_changed', {value});
 * }, [value]);
 * ```
 */
export const useComponentDidUpdate = <
    F extends ((...args: Array<any>) => any) | (() => any),
    A extends Array<any>,
>(
    func: F,
    deps: A,
) => {
    const isComponentDidMount = useRef(true);
    useEffect(() => {
        if (isComponentDidMount.current) {
            isComponentDidMount.current = false;
            return undefined;
        } else {
            return func();
        }
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};
